import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Upload, FileText, CheckCircle, XCircle, 
  ArrowLeft, Edit3, Save, AlertTriangle, Loader2,
  Trash2, Plus, Zap, Target, BarChart3, Eye
} from 'lucide-react';

export default function AnswerSheetEvaluator({ onNavigate }) {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedMarks, setEditedMarks] = useState({});
  const [error, setError] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size should be less than 10MB');
      return;
    }

    setError('');
    setUploadedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStep(2);
  };

  const handleEvaluate = async () => {
    if (!uploadedFile) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      const res = await api.post('/parent/evaluate-answer-sheet', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for AI processing
      });
      
      setEvaluation(res.data);
      setEditedMarks(
        res.data.answers.reduce((acc, a, i) => ({ ...acc, [i]: a.marks_awarded }), {})
      );
      setStep(3);
    } catch (err) {
      console.error('Failed to evaluate:', err);
      setError(err.response?.data?.detail || 'Failed to evaluate answer sheet. Please try again.');
    }
    setLoading(false);
  };

  const handleSaveMarks = async () => {
    setLoading(true);
    try {
      const updatedAnswers = evaluation.answers.map((a, i) => ({
        ...a,
        marks_awarded: editedMarks[i]
      }));
      
      const formData = new FormData();
      formData.append('evaluation_id', evaluation.id);
      formData.append('answers', JSON.stringify(updatedAnswers));
      
      await api.post('/parent/save-evaluation', formData);
      
      setEvaluation(prev => ({
        ...prev,
        answers: updatedAnswers,
        total_marks: Object.values(editedMarks).reduce((a, b) => a + b, 0)
      }));
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save:', err);
      setError('Failed to save changes');
    }
    setLoading(false);
  };

  const resetEvaluator = () => {
    setStep(1);
    setUploadedFile(null);
    setPreviewUrl(null);
    setEvaluation(null);
    setEditedMarks({});
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const totalMaxMarks = evaluation?.answers?.reduce((a, b) => a + (b.max_marks || 1), 0) || 0;
  const totalAwarded = Object.values(editedMarks).reduce((a, b) => a + b, 0);
  const percentage = totalMaxMarks > 0 ? Math.round((totalAwarded / totalMaxMarks) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="answer-sheet-evaluator">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : onNavigate('parent-dashboard')} 
            className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-slate-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Answer Sheet Evaluator</h1>
            <p className="text-xs text-slate-500">AI-powered grading</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Step 1: Upload */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-10 h-10 text-violet-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Upload Answer Sheet</h2>
              <p className="text-slate-400 text-sm">Take a photo or upload an image of the handwritten answer sheet</p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="file-input"
            />

            <div className="space-y-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white rounded-xl py-4 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
                data-testid="upload-btn"
              >
                <Upload className="w-5 h-5" /> Upload Image
              </button>
              
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.capture = 'environment';
                  input.onchange = (e) => handleFileUpload(e);
                  input.click();
                }}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-4 font-semibold flex items-center justify-center gap-2 transition-all"
                data-testid="camera-btn"
              >
                <Camera className="w-5 h-5" /> Take Photo
              </button>
            </div>

            {/* Tips */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-300 font-medium mb-2">Tips for best results:</p>
                  <ul className="text-slate-400 space-y-1.5">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Ensure good, even lighting
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Keep the sheet flat and centered
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Make sure all answers are visible
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Clear handwriting works best
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">How it works</h3>
              <div className="space-y-4">
                {[
                  { icon: Upload, label: 'Upload', desc: 'Take a photo of the answer sheet' },
                  { icon: Eye, label: 'AI Analysis', desc: 'Our AI reads and evaluates each answer' },
                  { icon: BarChart3, label: 'Results', desc: 'Get detailed scores and feedback' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{step.label}</p>
                      <p className="text-xs text-slate-500">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Preview & Confirm */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-xl font-bold text-white mb-2">Preview</h2>
              <p className="text-slate-400 text-sm">Confirm the image is clear before evaluating</p>
            </div>

            {previewUrl && (
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900">
                <img 
                  src={previewUrl} 
                  alt="Answer sheet" 
                  className="w-full max-h-[400px] object-contain"
                  onClick={() => setShowPreviewModal(true)}
                />
                <button
                  onClick={resetEvaluator}
                  className="absolute top-3 right-3 bg-red-500/80 hover:bg-red-500 p-2 rounded-lg transition-all"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
                <div className="absolute bottom-3 right-3 bg-slate-900/80 px-2 py-1 rounded text-xs text-slate-400">
                  Tap to zoom
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3.5 font-medium flex items-center justify-center gap-2 transition-all"
              >
                <Plus className="w-4 h-4" /> Re-upload
              </button>
              <button
                onClick={handleEvaluate}
                disabled={loading}
                className="bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 transition-all"
                data-testid="evaluate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" /> Evaluate
                  </>
                )}
              </button>
            </div>

            {loading && (
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 text-center">
                <p className="text-sm text-violet-300">AI is analyzing the answer sheet...</p>
                <p className="text-xs text-slate-500 mt-1">This may take 15-30 seconds</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Step 3: Results */}
        {step === 3 && evaluation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Score Summary */}
            <div className={`bg-gradient-to-br rounded-2xl p-6 text-center ${
              percentage >= 80 
                ? 'from-emerald-600/20 to-emerald-600/5 border border-emerald-500/30'
                : percentage >= 50 
                  ? 'from-amber-600/20 to-amber-600/5 border border-amber-500/30'
                  : 'from-red-600/20 to-red-600/5 border border-red-500/30'
            }`}>
              {/* Score Ring */}
              <div className="relative w-28 h-28 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="56" cy="56" r="50"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-slate-800"
                  />
                  <circle
                    cx="56" cy="56" r="50"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${percentage * 3.14} 314`}
                    strokeLinecap="round"
                    className={`transition-all duration-1000 ${
                      percentage >= 80 ? 'text-emerald-500' : percentage >= 50 ? 'text-amber-500' : 'text-red-500'
                    }`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{percentage}%</span>
                </div>
              </div>

              <p className="text-slate-400 text-sm mb-1">Total Score</p>
              <p className="text-3xl font-bold text-white">
                {totalAwarded}<span className="text-xl text-slate-400">/{totalMaxMarks}</span>
              </p>

              {/* XP earned */}
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-800/50">
                <Zap className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-violet-300">+15 XP earned</span>
              </div>
            </div>

            {/* Overall Feedback */}
            {evaluation.overall_feedback && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-300">{evaluation.overall_feedback}</p>
              </div>
            )}

            {/* Edit Toggle */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Answer Details</h3>
              <button
                onClick={() => editMode ? handleSaveMarks() : setEditMode(true)}
                disabled={loading}
                className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
                  editMode 
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white' 
                    : 'bg-slate-800 hover:bg-slate-700 text-white'
                }`}
              >
                {editMode ? (
                  loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>
                ) : (
                  <><Edit3 className="w-4 h-4" /> Edit Marks</>
                )}
              </button>
            </div>

            {/* Answer List */}
            <div className="space-y-3">
              {evaluation.answers.map((answer, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-slate-900/50 border rounded-xl p-4 ${
                    answer.is_correct 
                      ? 'border-emerald-500/30' 
                      : answer.is_partial 
                        ? 'border-amber-500/30' 
                        : 'border-red-500/30'
                  }`}
                  data-testid={`answer-${i}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-slate-800 text-slate-400 px-2.5 py-1 rounded-lg font-medium">
                        Q{answer.question_number || i + 1}
                      </span>
                      {answer.is_correct ? (
                        <div className="flex items-center gap-1 text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs font-medium">Correct</span>
                        </div>
                      ) : answer.is_partial ? (
                        <div className="flex items-center gap-1 text-amber-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs font-medium">Partial</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span className="text-xs font-medium">Wrong</span>
                        </div>
                      )}
                    </div>
                    
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max={answer.max_marks}
                          value={editedMarks[i]}
                          onChange={(e) => setEditedMarks(prev => ({
                            ...prev,
                            [i]: Math.min(Math.max(0, parseInt(e.target.value) || 0), answer.max_marks)
                          }))}
                          className="w-14 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-center text-sm focus:border-violet-500 outline-none"
                        />
                        <span className="text-slate-400 text-sm">/ {answer.max_marks}</span>
                      </div>
                    ) : (
                      <span className={`text-lg font-bold ${
                        editedMarks[i] === answer.max_marks 
                          ? 'text-emerald-400' 
                          : editedMarks[i] > 0 
                            ? 'text-amber-400' 
                            : 'text-red-400'
                      }`}>
                        {editedMarks[i]}/{answer.max_marks}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Student&apos;s Answer:</p>
                      <p className="text-white">{answer.student_answer || 'Not detected'}</p>
                    </div>
                    {!answer.is_correct && answer.correct_answer && (
                      <div className="bg-emerald-500/10 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Expected Answer:</p>
                        <p className="text-emerald-300">{answer.correct_answer}</p>
                      </div>
                    )}
                    {answer.feedback && (
                      <p className="text-xs text-slate-400 italic px-1">{answer.feedback}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={resetEvaluator}
                className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3.5 font-medium transition-all"
              >
                Evaluate Another
              </button>
              <button
                onClick={() => onNavigate('parent-dashboard')}
                className="bg-violet-600 hover:bg-violet-500 text-white rounded-xl py-3.5 font-semibold transition-all"
              >
                Done
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {showPreviewModal && previewUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreviewModal(false)}
          >
            <button 
              className="absolute top-4 right-4 text-white/70 hover:text-white"
              onClick={() => setShowPreviewModal(false)}
            >
              <XCircle className="w-8 h-8" />
            </button>
            <img 
              src={previewUrl} 
              alt="Full preview" 
              className="max-w-full max-h-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
